'use client'

import { useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { useTranslations, useLocale } from 'next-intl'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import styles from '@/styles/coming-soon.module.css'
import { BreadcrumbSchema } from '@/lib/schema'
import Link from 'next/link'
import { useActivityVisibility } from '@/hooks/useActivityVisibility'

const LODGE_ID = 'apex-premium-lodge'
const HERO_IMAGE = '/images/lodges/apex-premium-lodge/coming-soon/hero-farellones-panoramic.webp'

export default function ApexPremiumLodgePage() {
  const t = useTranslations('lodges')
  const tCommon = useTranslations('common')
  const locale = useLocale()
  const { isVisible, loaded } = useActivityVisibility()
  const router = useRouter()
  const heroImgRef = useRef<HTMLImageElement>(null)
  const heroBgRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (loaded && !isVisible(LODGE_ID, true)) {
      router.replace(`/${locale}/lodges`)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded])

  useEffect(() => {
    // Show previous hero as instant placeholder while new one loads
    try {
      const prev = sessionStorage.getItem('apex_last_hero')
      if (prev && (prev.startsWith('/images/') || prev.startsWith('https://')) && heroBgRef.current) {
        heroBgRef.current.style.backgroundImage = `url("${prev}")`
        heroBgRef.current.style.backgroundSize = 'cover'
        heroBgRef.current.style.backgroundPosition = 'center'
      }
    } catch {}

    const el = heroImgRef.current
    if (!el) return

    // Image may already be loaded from cache before onLoad handler was attached
    if (el.complete && el.naturalWidth > 0) {
      el.style.opacity = '1'
      try { sessionStorage.setItem('apex_last_hero', HERO_IMAGE) } catch {}
    }

    const onScroll = () => {
      el.style.transform = `translateY(${window.scrollY * 0.5}px)`
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  if (loaded && !isVisible(LODGE_ID, true)) {
    return null
  }

  // Get translated lodge data
  const lodgeData = {
    name: t('apex-premium-lodge.name'),
    subtitle: t('apex-premium-lodge.subtitle'),
    location: t('apex-premium-lodge.location'),
  }

  const cs = t.raw('apex-premium-lodge.comingSoon') as Record<string, string>

  return (
    <>
      <BreadcrumbSchema items={[
        { name: tCommon('home'), href: `/${locale}/` },
        { name: tCommon('lodges'), href: `/${locale}/lodges/` },
        { name: lodgeData.name, href: `/${locale}/lodges/apex-premium-lodge/` },
      ]} />
      <Header />

      <div className={styles.comingSoonPage}>
        {/* Hero Section */}
        <section className={styles.heroSection}>
          <div ref={heroBgRef} className={styles.heroBackground}>
            <img
              ref={heroImgRef}
              className={styles.heroImage}
              src={HERO_IMAGE}
              alt={cs.altHero}
              fetchPriority="high"
              onLoad={(e) => {
                const img = e.currentTarget as HTMLImageElement
                requestAnimationFrame(() => { img.style.opacity = '1' })
                try { sessionStorage.setItem('apex_last_hero', HERO_IMAGE) } catch {}
              }}
              style={{ opacity: 0, transition: 'opacity 0.5s ease' }}
            />
            <div className={styles.heroOverlay}></div>
          </div>
          <div className={styles.heroContent}>
            <span className={styles.comingSoonBadge}>{cs.badge}</span>
            <h1 className={styles.heroTitle}>{lodgeData.name}</h1>
            <p className={styles.heroSubtitle}>{lodgeData.subtitle}</p>
          </div>
        </section>

        {/* Location Section */}
        <section className={styles.locationSection}>
          <div className="container">
            <div className={styles.locationGrid}>
              <div className={styles.locationInfo}>
                <span className={styles.locationLabel}>{cs.locationLabel}</span>
                <h2 className={styles.locationTitle}>
                  {cs.locationTitle}
                </h2>
                <p className={styles.locationDescription}>
                  {cs.locationDescription}
                </p>
                <div className={styles.statsRow}>
                  <div className={styles.stat}>
                    <span className={styles.statNumber}>2,340m</span>
                    <span className={styles.statLabel}>{cs.statElevation}</span>
                  </div>
                  <div className={styles.stat}>
                    <span className={styles.statNumber}>45m</span>
                    <span className={styles.statLabel}>{cs.statFromSantiago}</span>
                  </div>
                  <div className={styles.stat}>
                    <span className={styles.statNumber}>3</span>
                    <span className={styles.statLabel}>{cs.statSkiCenters}</span>
                  </div>
                </div>
              </div>
              <div className={styles.locationImages}>
                <div className={styles.imageGrid}>
                  <img
                    className={styles.gridImage}
                    src="/images/lodges/apex-premium-lodge/coming-soon/farellones-village-snow.webp"
                    alt={cs.altVillage}
                    loading="lazy"
                  />
                  <img
                    className={styles.gridImage}
                    src="/images/lodges/apex-premium-lodge/coming-soon/andes-ski-slopes.webp"
                    alt={cs.altSlopes}
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Map Section */}
        <section className={styles.mapSection}>
          <div className={styles.mapContent}>
            <div className={styles.mapPlaceholder}>
              <img
                className={styles.mapImage}
                src="/images/lodges/apex-premium-lodge/coming-soon/farellones-map-aerial.webp"
                alt={cs.altMap}
                loading="lazy"
              />
              <div className={styles.mapOverlay}>
                <div className={styles.mapPin}>
                  <svg width="32" height="40" viewBox="0 0 32 40" fill="currentColor">
                    <path d="M16 0C7.163 0 0 7.163 0 16c0 12 16 24 16 24s16-12 16-24c0-8.837-7.163-16-16-16zm0 22a6 6 0 110-12 6 6 0 010 12z"/>
                  </svg>
                </div>
                <span className={styles.mapLabel}>{cs.mapLabel}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Gallery Section */}
        <section className={styles.gallerySection}>
          <div className="container">
            <h2 className={styles.gallerySectionTitle}>{cs.galleryTitle}</h2>
            <div className={styles.galleryGrid}>
              <div className={styles.galleryItem}>
                <img
                  src="/images/lodges/apex-premium-lodge/coming-soon/farellones-village-snow.webp"
                  alt={cs.altVillageSnow}
                  loading="lazy"
                />
                <span className={styles.galleryCaption}>{cs.captionVillage}</span>
              </div>
              <div className={styles.galleryItem}>
                <img
                  src="/images/lodges/apex-premium-lodge/coming-soon/andes-ski-slopes.webp"
                  alt={cs.altPristineSlopes}
                  loading="lazy"
                />
                <span className={styles.galleryCaption}>{cs.captionSlopes}</span>
              </div>
              <div className={styles.galleryItem}>
                <img
                  src="/images/lodges/apex-premium-lodge/coming-soon/hero-farellones-panoramic.webp"
                  alt={cs.altPanoramic}
                  loading="lazy"
                />
                <span className={styles.galleryCaption}>{cs.captionMountain}</span>
              </div>
            </div>
          </div>
        </section>
        {/* CTA Section */}
        <section className={styles.ctaSection}>
          <div className={styles.ctaContent}>
            <h2 className={styles.ctaTitle}>
              {cs.ctaTitle}
            </h2>
            <p className={styles.ctaDescription}>
              {cs.ctaDescription}
            </p>
            <Link href={`/${locale}/contact`} className={styles.ctaButton}>
              {tCommon('contactUs')}
            </Link>
          </div>
        </section>
      </div>

      <Footer />
    </>
  )
}
